import { history } from '@umijs/max';
import { useSession } from '@zhiyue/core';
import { Button, Result, Spin } from 'antd';
import React from 'react';
import { OpenPlatformAccessTokenContext } from '@/contexts/openPlatform';
import {
  clearOpenPlatformToken,
  fetchOpenPlatformToken,
  type OpenPlatformTokenResult,
  setOpenPlatformAccessToken,
} from '@/services/openPlatformToken';

const publicPaths = ['/user/login', '/callback'];
const normalizeText = (value: unknown) => String(value ?? '').trim();

const OpenPlatformBootstrap: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { currentUser, loading } = useSession();
  const [readyKey, setReadyKey] = React.useState('');
  const [errorKey, setErrorKey] = React.useState('');
  const [retryCount, setRetryCount] = React.useState(0);
  const [tokenInfo, setTokenInfo] =
    React.useState<OpenPlatformTokenResult | null>(null);
  const tenantUuid = normalizeText(currentUser?.tenantUuid);
  const organizationUuid = normalizeText(
    (currentUser as (typeof currentUser & { organizationUuid?: string }) | null)
      ?.organizationUuid,
  );
  const memberUuid = normalizeText(currentUser?.memberUuid);
  const userKey = [tenantUuid, organizationUuid, memberUuid].join('|');
  const bootstrapUser = React.useMemo(
    () => (currentUser ? { tenantUuid, organizationUuid, memberUuid } : null),
    [currentUser ? userKey : ''],
  );
  const isPublicPath = publicPaths.includes(history.location.pathname);

  React.useEffect(() => {
    if (loading || isPublicPath) return;
    if (!bootstrapUser) {
      clearOpenPlatformToken();
      setReadyKey('');
      setErrorKey('');
      setTokenInfo(null);
      return;
    }

    let cancelled = false;
    setErrorKey('');
    void fetchOpenPlatformToken(bootstrapUser)
      .then((result) => {
        if (!cancelled) {
          setOpenPlatformAccessToken(result.accessToken);
          setTokenInfo(result);
          setReadyKey(userKey);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTokenInfo(null);
          setErrorKey(userKey);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [bootstrapUser, isPublicPath, loading, retryCount, userKey]);

  React.useEffect(() => {
    if (!bootstrapUser || readyKey !== userKey || !tokenInfo) return;

    const refreshDelay = Math.max(tokenInfo.expiresAt - Date.now() - 60_000, 0);
    const timer = window.setTimeout(() => {
      clearOpenPlatformToken();
      void fetchOpenPlatformToken(bootstrapUser)
        .then((result) => {
          setOpenPlatformAccessToken(result.accessToken);
          setTokenInfo(result);
        })
        .catch(() => {
          setTokenInfo(null);
          setReadyKey('');
          setErrorKey(userKey);
        });
    }, refreshDelay);

    return () => window.clearTimeout(timer);
  }, [bootstrapUser, readyKey, tokenInfo, userKey]);

  if (isPublicPath || (!loading && !currentUser)) {
    return (
      <OpenPlatformAccessTokenContext.Provider value="">
        {children}
      </OpenPlatformAccessTokenContext.Provider>
    );
  }

  if (errorKey === userKey) {
    return (
      <Result
        status="error"
        title="应用初始化失败"
        extra={
          <Button
            type="primary"
            onClick={() => {
              clearOpenPlatformToken();
              setReadyKey('');
              setErrorKey('');
              setTokenInfo(null);
              setRetryCount((value) => value + 1);
            }}
          >
            重试
          </Button>
        }
      />
    );
  }

  if (loading || readyKey !== userKey || !tokenInfo) {
    return <Spin spinning size="large" fullscreen />;
  }

  return (
    <OpenPlatformAccessTokenContext.Provider value={tokenInfo.accessToken}>
      {children}
    </OpenPlatformAccessTokenContext.Provider>
  );
};

export default OpenPlatformBootstrap;
